import ApiService from "../../services/ApiService";
import translate from "../../services/Translate";

export const COLOR_PALETTE = [
  "var(--color-blue)",
  "var(--color-green)",
  "var(--color-dark-red)",
  "var(--color-dark-green)",
  "var(--color-electric-blue)",
  "var(--color-orange)",
  "var(--color-pink)",
  "var(--color-violet)",
  "var(--color-dark-brown)",
];

export const sortCohortsWithDeletedLast = (cohorts: { id: string; name: string }[]) => {
  return cohorts.filter(Boolean).sort((a, b) => {
    const aHasName = a.name && a.name.trim() !== "";
    const bHasName = b.name && b.name.trim() !== "";

    if (aHasName && !bHasName) return -1;
    if (!aHasName && bHasName) return 1;
    if (aHasName && bHasName) return a.name.localeCompare(b.name);
    return String(a.id).localeCompare(String(b.id));
  });
};

export const fetchQuestions = (
  surveyTypeQuestionId: string,
  setSurveyQuestions: Function,
  setSurveyQuestionsSpinner: Function
) => {
  ApiService.get(`surveys/types/questions/${surveyTypeQuestionId}`).then((response) => {
    setSurveyQuestions(response.data.survey_questions);
    setSurveyQuestionsSpinner(false);
  });
};

export const fetchAnswer = (
  surveyQuestionId: string,
  setSurveyAnswer: Function,
  setSurveyAnswerSpinner: Function,
  filters?: Record<string, string>
) => {
  let url = `surveys/questions/${surveyQuestionId}/answers`;

  if (filters && Object.keys(filters).length > 0) {
    const params = new URLSearchParams();
    if (filters.cohort_id) params.append("cohort_ids", filters.cohort_id);
    if (filters.cohort_program) params.append("program_names", filters.cohort_program);
    if (filters.project_id) params.append("project_ids", filters.project_id);
    if (filters.meeting_id) params.append("meeting_ids", filters.meeting_id);
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
  } else {
    url += `?fetch_cohort_name=true`;
  }

  ApiService.get(url).then((response) => {
    const answers = response.data.survey_answers.map((answer: any) => ({
      ...answer,
      content: answer.content,
    }));
    setSurveyAnswer(answers);
    setSurveyAnswerSpinner(false);
  });
};

export const getChartData = (surveyAnswer: any, surveyType: any) => {
  if (!surveyAnswer || surveyAnswer.length === 0) return [];

  const responseCounts: { [key: string]: number } = surveyAnswer.reduce(
    (acc: { [key: string]: number }, answer: any) => {
      const response = answer.content.answer;
      acc[response] = (acc[response] || 0) + 1;
      return acc;
    },
    {}
  );

  const totalResponses = Object.values(responseCounts).reduce((sum, count) => sum + count, 0);

  let chartData = Object.entries(responseCounts).map(([response, count], index) => ({
    response,
    count,
    percentage: ((count / totalResponses) * 100).toFixed(2),
    color: COLOR_PALETTE[index % COLOR_PALETTE.length],
  }));

  chartData = chartData.sort((a, b) => parseFloat(a.response) - parseFloat(b.response));

  if (surveyType === "question") {
    chartData = chartData.slice(0, 10);
  }

  return chartData;
};

export const calculateGlobalNote = (surveyAnswer: any) => {
  if (!surveyAnswer.length) return 0;

  const total = surveyAnswer.reduce((sum: number, answer: any) => sum + parseFloat(answer.content.answer || 0), 0);
  return (total / surveyAnswer.length).toFixed(2);
};

export const downloadSurveyAnswersCsv = (
  surveyLanguage: string,
  surveyType: string,
  language: string,
  setIsCsvSurveyDownloading: Function,
  dateFrom?: string,
  dateTo?: string,
  getMeetingStartDate?: boolean
) => {
  setIsCsvSurveyDownloading(true);

  let url = `surveys/${surveyType}/${surveyLanguage}/answers/download?get_meeting_dt_start=${getMeetingStartDate}`;
  const queryParams = new URLSearchParams();

  if (dateFrom) queryParams.append("date_from", dateFrom);
  if (dateTo) queryParams.append("date_to", dateTo);

  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }

  ApiService.get(url)
    .then((resp) => {
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(
        new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      );
      link.setAttribute("download", `${surveyType}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    })
    .catch((err) => {
      console.error(err);
      alert(translate(language, "PAGES.INSIGHTS.SURVEY.DOWNLOADING_CSV_ERROR"));
    })
    .finally(() => setIsCsvSurveyDownloading(false));
};

export const updateFilters = (
  field: string,
  values: string[],
  setActiveFilters: Function,
  surveySelection: Array<any>,
  setSurveyAnswerSpinner: Function,
  surveyQuestions: Array<any>,
  setSurveyAnswer: Function
) => {
  setActiveFilters((activeFilters: Record<string, string>) => {
    const newFilters = {
      ...activeFilters,
      [field]: values.join(",") || "",
    };

    Object.keys(newFilters).forEach((key) => {
      if (!newFilters[key]) {
        delete newFilters[key];
      }
    });

    if (surveySelection[0]) {
      setSurveyAnswerSpinner(true);
      const selectedQuestion = surveyQuestions.find((survey) => survey.text === surveySelection[0].label);
      if (selectedQuestion) {
        fetchAnswer(selectedQuestion.question_id, setSurveyAnswer, setSurveyAnswerSpinner, newFilters);
      }
    }

    return newFilters;
  });
};

export const getFilteredCohortNames = (
  allCohortNames: { id: string; name: string }[],
  relatedCohortPrograms: { cohort_id: string; program: string }[],
  selectedPrograms: string[],
  relatedCohortMeetings?: { cohort_id: string; meeting_id: string }[],
  selectedMeetingIds: string[] = []
) => {
  let filtered = allCohortNames;

  if (selectedPrograms.length > 0) {
    const validCohortIdsFromPrograms = new Set(
      relatedCohortPrograms.filter((rel) => selectedPrograms.includes(rel.program)).map((rel) => rel.cohort_id)
    );
    filtered = filtered.filter((cohort) => validCohortIdsFromPrograms.has(cohort.id));
  }

  if (selectedMeetingIds.length > 0 && relatedCohortMeetings) {
    const validCohortIdsFromMeetings = new Set(
      relatedCohortMeetings.filter((rel) => selectedMeetingIds.includes(rel.meeting_id)).map((rel) => rel.cohort_id)
    );
    filtered = filtered.filter((cohort) => validCohortIdsFromMeetings.has(cohort.id));
  }

  return filtered;
};

export const getFilteredCohortPrograms = (
  allCohortPrograms: string[],
  relatedCohortPrograms: { cohort_id: string; program: string }[],
  selectedCohortIds: string[],
  relatedProgramMeetings?: { program: string; meeting_id: string }[],
  selectedMeetingIds: string[] = []
) => {
  let filtered = allCohortPrograms;

  if (selectedCohortIds.length > 0) {
    const validProgramsFromCohorts = new Set(
      relatedCohortPrograms.filter((rel) => selectedCohortIds.includes(rel.cohort_id)).map((rel) => rel.program)
    );
    filtered = filtered.filter((program) => validProgramsFromCohorts.has(program));
  }

  if (selectedMeetingIds.length > 0 && relatedProgramMeetings) {
    const validProgramsFromMeetings = new Set(
      relatedProgramMeetings.filter((rel) => selectedMeetingIds.includes(rel.meeting_id)).map((rel) => rel.program)
    );
    filtered = filtered.filter((program) => validProgramsFromMeetings.has(program));
  }

  return filtered;
};

export const getFilteredMeetings = (
  allMeetings: { id: string; name: string }[],
  relatedCohortMeetings: { cohort_id: string; meeting_id: string }[] = [],
  relatedProgramMeetings: { program: string; meeting_id: string }[] = [],
  selectedCohortIds: string[] = [],
  selectedPrograms: string[] = []
) => {
  if (selectedCohortIds.length === 0 && selectedPrograms.length === 0) {
    return allMeetings;
  }

  const meetingIdsFromCohorts = new Set(
    relatedCohortMeetings
      .filter((rel) => (selectedCohortIds.length ? selectedCohortIds.includes(rel.cohort_id) : true))
      .map((rel) => rel.meeting_id)
  );

  const meetingIdsFromPrograms = new Set(
    relatedProgramMeetings
      .filter((rel) => (selectedPrograms.length ? selectedPrograms.includes(rel.program) : true))
      .map((rel) => rel.meeting_id)
  );

  let validMeetingIds: Set<string>;
  if (selectedCohortIds.length > 0 && selectedPrograms.length > 0) {
    validMeetingIds = new Set(Array.from(meetingIdsFromCohorts).filter((id) => meetingIdsFromPrograms.has(id)));
  } else if (selectedCohortIds.length > 0) {
    validMeetingIds = meetingIdsFromCohorts;
  } else {
    validMeetingIds = meetingIdsFromPrograms;
  }

  return allMeetings.filter((meeting) => validMeetingIds.has(meeting.id));
};

export const resetAndFetchSurveyData = (
  setActiveFilters: Function,
  surveySelection: Array<any>,
  surveyQuestions: Array<any>,
  setSurveyAnswerSpinner: Function,
  setSurveyAnswer: Function,
  setFilterData: Function,
  setOriginalFilterData?: Function
) => {
  setActiveFilters({});
  if (surveySelection[0]) {
    const selectedQuestion = surveyQuestions.find((survey) => survey.text === surveySelection[0].label);
    if (selectedQuestion) {
      setSurveyAnswerSpinner(true);
      setSurveyAnswer([]);
      setFilterData({
        projects: [],
        cohort_names: [],
        cohort_programs: [],
        meetings: [],
        cohort_program_relationships: [],
      });

      fetchAnswer(
        selectedQuestion.question_id,
        (answers: any[]) => {
          setSurveyAnswer(answers);

          const relatedCohortPrograms = answers
            .filter((answer) => answer.cohort_id && answer.cohort_program)
            .map((answer) => ({
              cohort_id: String(answer.cohort_id),
              program: answer.cohort_program as string,
            }))
            .filter(
              (rel, index, self) =>
                self.findIndex((r) => r.cohort_id === rel.cohort_id && r.program === rel.program) === index
            );

          const relatedCohortMeetings = answers
            .filter((answer) => answer.cohort_id && answer.meeting_id)
            .map((answer) => ({
              cohort_id: String(answer.cohort_id),
              meeting_id: String(answer.meeting_id),
            }))
            .filter(
              (rel, index, self) =>
                self.findIndex((r) => r.cohort_id === rel.cohort_id && r.meeting_id === rel.meeting_id) === index
            );

          const relatedProgramMeetings = answers
            .filter((answer) => answer.cohort_program && answer.meeting_id)
            .map((answer) => ({
              program: String(answer.cohort_program),
              meeting_id: String(answer.meeting_id),
            }))
            .filter(
              (rel, index, self) =>
                self.findIndex((r) => r.program === rel.program && r.meeting_id === rel.meeting_id) === index
            );

          const completeFilterData = {
            projects: Array.from(new Set(answers.map((answer) => answer.project_id))),
            cohort_names: Array.from(new Set(answers.map((answer) => String(answer.cohort_id)))).map((id) => ({
              id,
              name: answers.find((answer) => String(answer.cohort_id) === id)?.cohort_name || "",
            })),
            cohort_programs: Array.from(new Set(answers.map((answer) => answer.cohort_program))),
            meetings: Array.from(new Set(answers.map((answer) => answer.meeting_id))).map((id) => ({
              id,
              name: answers.find((answer) => answer.meeting_id === id)?.meeting_name || "",
            })),
            cohort_program_relationships: relatedCohortPrograms,
            cohort_meeting_relationships: relatedCohortMeetings,
            program_meeting_relationships: relatedProgramMeetings,
          };

          setFilterData(completeFilterData);
          if (setOriginalFilterData) {
            setOriginalFilterData(completeFilterData);
          }
        },
        setSurveyAnswerSpinner
      );
    }
  }
};
